<template>
    <div class="table-panel p-4">
        <div class="table-title-row bg-gray-50 flex align-items-center justify-content-center relative">
            <span class="table-title Body-S-Bold">Grade list</span>
            <Button v-if="selectedRows.length > 1" label="Delete" icon="pi pi-trash" severity="danger" size="small"
                class="absolute" style="right: 20px; top: 50%; transform: translateY(-50%);"
                @click="emit('delete-selected', selectedRows)" />
        </div>

        <DataTable :value="tableData" :loading="isLoading" class="p-datatable-sm" :paginator="true" :rows="rowsPerPage"
            :totalRecords="totalRecords" :first="(currentPage - 1) * rowsPerPage" :rowsPerPageOptions="[5, 10, 20]"
            :lazy="true" @page="onPage" style="cursor: pointer;">
            <Column field="checked" header="" style="width: 40px;">
                <template #header>
                    <Checkbox v-model="selectAll" :binary="true" @change="onSelectAll" class="accent-checkbox" />
                </template>
                <template #body="{ data }">
                    <Checkbox v-model="selectedRows" :value="data._id" class="accent-checkbox" />
                </template>
            </Column>

            <Column field="name" header="Grade" :bodyStyle="{ whiteSpace: 'nowrap' }" class="whitespace-nowrap" />

            <Column field="subjects" header="Subjects" style="width: 100%"
                :bodyStyle="{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '0' }"
                class="whitespace-nowrap">
                <template #body="{ data }">
                    <span class="subject-chips"
                        :title="Array.isArray(data.subjects) && data.subjects.length > 0 ? data.subjects.map((s) => s.name).join(', ') : ''">
                        {{
                            Array.isArray(data.subjects) && data.subjects.length > 0
                                ? data.subjects.map((s) => s.name).join(", ")
                                : " "
                        }}
                    </span>
                </template>
            </Column>

            <Column field="medium" header=" " style="width: 110px;" :bodyStyle="{ whiteSpace: 'nowrap' }"
                class="whitespace-nowrap">
                <template #body="{ data }">
                    {{ capitalize(data.medium) }}
                </template>
            </Column>

            <Column header="" style="width: 80px; text-align: right;" :bodyStyle="{ whiteSpace: 'nowrap' }"
                class="whitespace-nowrap">
                <template #body="{ data }">
                    <div class="action-buttons">
                        <!-- <Button icon="pi pi-pencil" class="p-button-text p-button-sm action-btn edit-btn"
                            @click.stop="emit('edit-grade', data)" v-tooltip.top="'Edit'" /> -->
                        <Button icon="pi pi-trash" class="p-button-text p-button-sm action-btn delete-btn"
                            @click.stop="emit('delete-grade', data)" v-tooltip.top="'Delete'" />
                    </div>
                </template>
            </Column>

            <template #paginatorstart>
                <div class="text-sm Caption">
                    Showing {{ tableData.length }} of {{ totalRecords }} records
                </div>
            </template>

            <template #empty>
                <div class="flex align-items-center justify-content-center" style="min-height: calc(100vh - 600px)">
                    <div class="text-center">
                        <i class="pi pi-inbox" style="font-size: 2rem; color: var(--surface-400)"></i>
                        <p class="mt-2 text-color-secondary">No data found</p>
                    </div>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";

const props = defineProps({
    tableData: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    rowsPerPage: { type: Number, default: 10 },
    totalRecords: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 },
});

const emit = defineEmits(["page-change", "edit-grade", "delete-grade", "delete-selected"]);

const selectedRows = ref([]);
const selectAll = ref(false);

const onSelectAll = () => {
    selectedRows.value = selectAll.value ? props.tableData.map((r) => r._id) : [];
};

const onPage = (event) => {
    const page = event.page + 1;
    const limit = event.rows;
    emit("page-change", { page, limit });
};

const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
</script>

<style scoped>
/* Keeping your existing styles exactly as they were */
.table-panel {
    width: 100%;
    background-color: white;
    border-radius: 5px;
}

.table-title-row {
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--surface-200, #e5e7eb);
    text-align: center;
}

.table-title {
    color: var(--gray-800);
}

.subject-chips {
    color: var(--gray-600);
    font-size: 13px;
}

.action-buttons {
    display: flex;
    gap: 4px;
    justify-content: flex-end;
}

.action-btn {
    padding: 4px !important;
    width: 28px !important;
    height: 28px !important;
}

.edit-btn {
    color: var(--primary-500, #6366f1) !important;
}

.delete-btn {
    color: var(--red-500, #ef4444) !important;
}

.edit-btn:hover {
    background: var(--primary-50, #eef2ff) !important;
}

.delete-btn:hover {
    background: var(--red-50, #fef2f2) !important;
}

.p-datatable .p-column-header-content {
    justify-content: space-between;
}

:deep(.p-datatable-thead > tr > th) {
    background-color: #F9FAFB !important;
}

:deep(.p-paginator) {
    background-color: #F9FAFB !important;
}

.p-select {
    --p-select-sm-font-size: 12px;
    --p-select-color: var(--black-700);
    --p-select-hover-border-color: transparent;
    --p-select-border-color: transparent;
    --p-select-focus-border-color: transparent;
}

::v-deep(.p-select-sm .p-select-label) {
    padding-left: 0 !important;
    padding-right: 0 !important;
}
</style>